import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wallet, LogOut, User as UserIcon, Layers, ShieldCheck, Menu, Library, Store, Building2, Sparkles } from "lucide-react";
import { shortAddr } from "@/lib/api";
import NotificationsBell from "@/components/NotificationsBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export default function Navbar() {
  const { user, logout, walletAddress, connectWallet } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const onConnect = async () => {
    try { await connectWallet(); } catch {}
  };

  const links = [
    { to: "/marketplace", label: "Market", icon: Store },
    { to: "/collections", label: "Collections", icon: Library },
    ...(user ? [{ to: "/dashboard", label: "Dashboard", icon: Layers }] : []),
    ...(user && user.role === "teacher" ? [{ to: "/dashboard/approvals", label: "Approvals", icon: ShieldCheck }] : []),
    { to: "/register-school", label: "Register School", icon: Building2 },
  ];

  return (
    <header className="sticky top-0 z-50 bevel-out" style={{ borderBottom: "2px solid #404040" }}>
      <div className="flag-bar" />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-start justify-between gap-3">
        {/* Left: logo on top, burger below */}
        <div className="flex flex-col items-start gap-2 flex-shrink-0">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 no-underline">
            <div
              className="w-14 h-14 grid place-items-center font-display font-bold text-4xl bevel-out"
              style={{ background: "linear-gradient(135deg, #ff00ff 0%, #ffff00 100%)", color: "#000" }}
            >
              C
            </div>
            <div className="leading-none">
              <div className="font-display text-4xl tracking-tight rainbow-text font-bold">
                Cure<span className="inline-block align-middle" style={{ transform: "rotate(90deg)", color: "#ff00ff", WebkitTextFillColor: "#ff00ff", display: "inline-block", lineHeight: 1 }}>8</span>r!
              </div>
              <div className="text-xs font-mono text-black/70 mt-1">~ since 2026 ~</div>
            </div>
          </Link>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="burger-menu-btn"
                className="btn-90 !py-1 !px-2 text-xs"
                aria-label="Open menu"
              >
                <Menu size={12} />
                <span className="font-mono">START</span>
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#c0c0c0] border-r-4 border-r-[#404040] text-black w-[300px] p-0">
              <SheetHeader className="p-0">
                <div className="window-title">
                  <SheetTitle className="text-white font-display tracking-tight flex items-center gap-2 text-sm">
                    <Sparkles size={14} /> Cure<span className="inline-block" style={{ transform: "rotate(90deg)", lineHeight: 1 }}>8</span>r Menu
                  </SheetTitle>
                  <span className="text-white font-mono text-xs">_ □ X</span>
                </div>
              </SheetHeader>
              <div className="flex flex-col py-3 px-3 gap-2">
                {links.map((l) => {
                  const Icon = l.icon;
                  const active = location.pathname === l.to || (l.to !== "/" && location.pathname.startsWith(l.to));
                  return (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`burger-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`btn-90 !justify-start !px-3 !py-2 ${active ? "is-active" : ""}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Icon size={14} />
                      <span className="font-display text-base">{l.label}</span>
                    </Link>
                  );
                })}
                <div className="divider-90" />
                {!user ? (
                  <div className="flex flex-col gap-2">
                    <button
                      data-testid="burger-signup-btn"
                      onClick={() => { setMobileOpen(false); navigate("/signup"); }}
                      className="btn-90 btn-90-primary !py-2"
                    >
                      ★ Sign Up FREE!
                    </button>
                    <button
                      data-testid="burger-login-btn"
                      onClick={() => { setMobileOpen(false); navigate("/login"); }}
                      className="btn-90 !py-2"
                    >
                      Login
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Link
                      to={`/profile/${user.id}`}
                      onClick={() => setMobileOpen(false)}
                      className="btn-90 !justify-start !px-3 !py-2"
                      style={{ textDecoration: "none" }}
                    >
                      <UserIcon size={14} /> My Profile
                    </Link>
                    <button
                      onClick={() => { setMobileOpen(false); logout(); }}
                      className="btn-90 !py-2"
                      style={{ background: "#ff0000", color: "#fff", borderColor: "#ffffff #800000 #800000 #ffffff" }}
                    >
                      <LogOut size={14} /> Logout
                    </button>
                  </div>
                )}
                <div className="text-[10px] font-mono text-black/70 text-center mt-2">
                  Best viewed in Netscape 4.0
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2 self-center">
          {user ? (
            <>
              <button
                data-testid="wallet-connect-btn"
                onClick={onConnect}
                className="btn-90 btn-90-link !py-1 text-xs"
              >
                <Wallet size={12} />
                <span className="hidden sm:inline">{walletAddress ? shortAddr(walletAddress) : "Connect"}</span>
              </button>
              <NotificationsBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-profile-btn" className="btn-90 !p-1 !w-9 !h-9 grid place-items-center">
                    <UserIcon size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#c0c0c0] border-2 border-black text-black rounded-none">
                  <DropdownMenuLabel className="text-black/70 text-xs font-mono">
                    {user.name} · {user.role}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-black/30" />
                  <DropdownMenuItem data-testid="nav-my-profile" onClick={() => navigate(`/profile/${user.id}`)} className="cursor-pointer font-display">
                    <UserIcon size={14} className="mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer font-display">
                    <Layers size={14} className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                  {user.role === "teacher" && (
                    <DropdownMenuItem onClick={() => navigate("/dashboard/approvals")} className="cursor-pointer font-display">
                      <ShieldCheck size={14} className="mr-2" /> Approvals
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-black/30" />
                  <DropdownMenuItem data-testid="nav-logout" onClick={logout} className="cursor-pointer text-red-700 font-display">
                    <LogOut size={14} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <button
                data-testid="nav-signup-btn"
                className="btn-90 btn-90-primary !py-1 text-sm"
                onClick={() => navigate("/signup")}
              >
                ★ Sign Up!
              </button>
              <button
                data-testid="nav-login-btn"
                className="btn-90 !py-1 text-sm"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
            </>
          )}
        </div>
      </div>
      {/* scrolling marquee */}
      <div className="marquee" data-testid="nav-marquee">
        <div className="marquee__track">
          ★ WELCOME TO CURE8R DOT COM ★ &nbsp;&nbsp;★&nbsp;&nbsp; MINT YOUR HOMEWORK ON THE BLOCKCHAIN ★ &nbsp;&nbsp; ERC-721 + ERC-1155 SUPPORTED ★ &nbsp;&nbsp; 97% TO THE CREATOR ★ &nbsp;&nbsp; NEW! TEACHER VERIFIED COLLECTIONS ★ &nbsp;&nbsp; THIS PAGE LAST UPDATED: TODAY ★ &nbsp;&nbsp; SIGN MY GUESTBOOK!
        </div>
      </div>
    </header>
  );
}
